import { api } from '@api'
import { useQuery } from '@tanstack/solid-query'
import { createEffect, createMemo, createSignal } from 'solid-js'
import { useCourses } from './useCourses'

export type SectionFilter = 'all' | 'active' | 'todo'

const fetchOverview = async (slug?: string) => {
  if (!slug) return null

  const { data, error } = await api()
    .from('courses')
    .select(`
      *,
      course_sections(
        *,
        course_lessons(
          *,
          course_progress(*)
        )
      )
    `)
    .eq('slug', slug)
    .order('index', { referencedTable: 'course_sections', ascending: true })
    .order('index', {
      referencedTable: 'course_sections.course_lessons',
      ascending: true,
    })
    .single()

  if (error) throw error
  return data
}

export function useCourseOverview() {
  const [activeCourseslug, setActiveCourseslug] = createSignal<string>()

  const { coursesQuery } = useCourses()

  const activeLessonOverviewQuery = useQuery(() => ({
    queryKey: ['course_overview', activeCourseslug()],
    queryFn: () => fetchOverview(activeCourseslug()),
    enabled: !!activeCourseslug(),
    staleTime: 1000 * 60 * 5,
  }))

  // 1. Derive the active course by checking the global cache first,
  // falling back to the detailed overview query.
  const activeCourse = () =>
    coursesQuery.data?.find((c) => c.slug === activeCourseslug()) ||
    activeLessonOverviewQuery.data

  // 2. Derive basic properties
  const courseTitle = () => activeCourse()?.title
  const courseId = () => activeCourse()?.id

  const isLocked = () => {
    const currentCourse = activeCourse()
    if (!currentCourse?.prerequisite_slug) return false

    const allCourses = coursesQuery.data
    if (!allCourses) return true

    const prereq = allCourses.find((c) => c.slug === currentCourse.prerequisite_slug)
    if (!prereq) return false

    return prereq.completed_lessons < prereq.total_lessons
  }

  // 3. Memoize the flattened array since flatMap is slightly heavier
  const allLessons = createMemo(() => {
    const sections = activeLessonOverviewQuery.data?.course_sections
    if (!sections) return []
    return sections.flatMap((s) => s.course_lessons || [])
  })

  // --- DERIVED COUNTS ---
  const totalSections = () => activeLessonOverviewQuery.data?.course_sections?.length || 0

  const totalLessonsCount = () => allLessons().filter((l) => l.type !== 'quiz').length

  const totalQuizzesCount = () => allLessons().filter((l) => l.type === 'quiz').length

  const totalItemsCount = () => allLessons().length

  const totalContentHours = () => {
    const totalItems = allLessons().length
    return `~${(totalItems * 0.2).toFixed(1)}h`
  }

  const completedCount = () =>
    allLessons().filter((l) => l.course_progress?.[0]?.status === 'completed').length

  // --- REFACTORED STATUS & PROGRESS ---
  const courseStatus = (): 'not_started' | 'in_progress' | 'completed' => {
    const total = totalItemsCount()
    if (total === 0) return 'not_started'

    const completed = completedCount()

    if (completed === total) return 'completed'

    const hasAnyProgress = allLessons().some(
      (l) => l.course_progress && l.course_progress.length > 0,
    )

    if (hasAnyProgress) return 'in_progress'
    return 'not_started'
  }

  const percentComplete = () => {
    const total = totalItemsCount()
    if (total === 0) return 0
    return Math.round((completedCount() / total) * 100)
  }

  const currentLessonSlug = () => {
    const lessons = allLessons()
    if (lessons.length === 0) return null

    const upToLesson = lessons.find((lesson) => {
      const progress = lesson.course_progress?.[0]
      return !progress || progress.status !== 'completed'
    })

    return upToLesson ? upToLesson.slug : lessons[lessons.length - 1].slug
  }

  const [filter, setFilter] = createSignal<SectionFilter>('all')
  const [openSections, setOpenSections] = createSignal<Record<string, boolean>>({})

  const sectionsWithState = createMemo(() => {
    const sections = activeLessonOverviewQuery.data?.course_sections
    if (!sections) return []

    const current = currentLessonSlug()

    return sections.map((section) => {
      const lessons = section.course_lessons.map((lesson) => ({
        id: lesson.id,
        slug: lesson.slug,
        title: lesson.title,
        type: lesson.type,
        done: lesson.course_progress?.[0]?.status === 'completed',
        current: !!current && lesson.slug === current,
      }))

      const total = lessons.length
      const done = lessons.filter((l) => l.done).length
      const topics = lessons.filter((l) => l.type !== 'quiz').length
      const quizzes = total - topics
      const containsCurrent = lessons.some((l) => l.current)
      const state: 'done' | 'active' | 'todo' =
        total > 0 && done === total ? 'done' : done > 0 || containsCurrent ? 'active' : 'todo'

      return {
        id: section.id,
        title: section.title,
        lessons,
        total,
        done,
        topics,
        quizzes,
        state,
        containsCurrent,
      }
    })
  })

  const defaultOpenSectionId = createMemo(() => {
    const sections = sectionsWithState()
    if (sections.length === 0) return undefined
    return (sections.find((s) => s.containsCurrent) || sections[0]).id
  })

  createEffect((prevSlug) => {
    const slug = activeCourseslug()
    if (slug !== prevSlug) {
      setOpenSections({})
    }
    return slug
  })

  const isSectionOpen = (id: string) => {
    const explicit = openSections()[id]
    return explicit ?? id === defaultOpenSectionId()
  }
  const setSectionOpen = (id: string, open: boolean) =>
    setOpenSections((prev) => ({ ...prev, [id]: open }))

  const visibleSections = createMemo(() =>
    sectionsWithState().filter((s) => filter() === 'all' || s.state === filter()),
  )

  const allVisibleOpen = createMemo(() => {
    const visible = visibleSections()
    return visible.length > 0 && visible.every((s) => isSectionOpen(s.id))
  })

  const toggleExpandAll = () => {
    const target = !allVisibleOpen()
    const next = { ...openSections() }
    visibleSections().forEach((s) => {
      next[s.id] = target
    })
    setOpenSections(next)
  }

  return {
    activeLessonOverviewQuery,
    activeCourseslug,
    setActiveCourseslug,

    sectionsWithState,
    visibleSections,
    filter,
    setFilter,
    isSectionOpen,
    setSectionOpen,
    allVisibleOpen,
    toggleExpandAll,

    // Derived Basic Info
    activeCourse,
    courseTitle,
    courseId,

    // Derived Counts
    totalSections,
    totalLessonsCount,
    totalQuizzesCount,
    totalItemsCount,
    completedCount,

    // Progress State
    currentLessonSlug,
    courseStatus,
    percentComplete,
    allLessons,
    isLocked,
    totalContentHours
  }
}
